import * as React from 'react';

import { Drawer } from '@/components/ui';
import { useAnalytics } from '@/lib/analytics/hooks/use-analytics';

/**
 * Props for OrientationDrawer component
 */
interface OrientationDrawerProps {
  /** Controls drawer visibility */
  isOpen: boolean;
  /** Callback when drawer is closed */
  onClose: () => void;
}

/**
 * YouTube Player state constants
 * Reference: https://developers.google.com/youtube/iframe_api_reference#Playback_status
 */
const YT_PLAYER_STATE = {
  PLAYING: 1,
  ENDED: 0,
} as const;

/**
 * YouTube IFrame Player API types
 */
interface YTPlayer {
  destroy: () => void;
}

interface YTPlayerEvent {
  data: number;
}

interface YTPlayerConfig {
  events?: {
    onStateChange?: (event: YTPlayerEvent) => void;
  };
}

interface YouTubeAPI {
  Player: new (elementId: string, config: YTPlayerConfig) => YTPlayer;
}

declare global {
  var YT: YouTubeAPI | undefined;

  var onYouTubeIframeAPIReady: (() => void) | undefined;
}

/**
 * YouTube video ID for dashboard orientation tour
 * Extracted from: https://youtu.be/IpeWjqqpDVA?si=p2LbQRQ3toOK9vyu
 *
 * To update: Change this constant and redeploy the application
 */
const ORIENTATION_VIDEO_ID = 'RdWZ3rb7p-E';

/**
 * YouTube embed configuration
 * Using privacy-enhanced mode (youtube-nocookie.com) to avoid tracking users
 * before they interact with the video
 */
const YOUTUBE_EMBED_PARAMS = '?rel=0'; // Prevent showing related videos

/**
 * Dashboard Orientation drawer component
 *
 * Displays a drawer with a YouTube video tutorial for dashboard orientation.
 * The video provides a tour of the dashboard features and functionality.
 *
 * @param props - Component props
 * @param props.isOpen - Controls drawer visibility
 * @param props.onClose - Callback when drawer is closed
 *
 * @example
 * ```tsx
 * const [isOpen, setIsOpen] = useState(false);
 * <OrientationDrawer isOpen={isOpen} onClose={() => setIsOpen(false)} />
 * ```
 */
export const OrientationDrawer: React.FC<OrientationDrawerProps> = ({
  isOpen,
  onClose,
}) => {
  const { trackEvent } = useAnalytics();
  const playerRef = React.useRef<YTPlayer | null>(null);
  const hasTrackedPlay = React.useRef(false);
  const hasTrackedComplete = React.useRef(false);
  const iframeId = 'orientation-video-iframe';

  /**
   * Load YouTube IFrame API script
   * Only loads once per page session
   */
  React.useEffect(() => {
    if (globalThis.YT) return;

    const tag = document.createElement('script');
    tag.src = 'https://www.youtube.com/iframe_api';
    const [firstScriptTag] = document.querySelectorAll('script');
    firstScriptTag?.parentNode?.insertBefore(tag, firstScriptTag);
  }, []);

  /**
   * Initialize YouTube player with event tracking when drawer opens
   */
  React.useEffect(() => {
    if (isOpen) {
      const initPlayer = () => {
        if (!globalThis.YT?.Player) return;

        // Reset tracking flags when drawer opens
        hasTrackedPlay.current = false;
        hasTrackedComplete.current = false;

        // Initialize player with event handlers
        playerRef.current = new globalThis.YT.Player(iframeId, {
          events: {
            onStateChange: event => {
              // Track when video starts playing (only once per drawer open)
              if (
                event.data === YT_PLAYER_STATE.PLAYING &&
                !hasTrackedPlay.current
              ) {
                trackEvent('DashboardOrientationVideoPlayed', 1, {
                  clickType: 'interaction',
                  clickCTA: 'Play Video',
                  clickSection: 'Dashboard Orientation Section',
                });
                hasTrackedPlay.current = true;
              }

              // Track when video completes (only once per drawer open)
              if (
                event.data === YT_PLAYER_STATE.ENDED &&
                !hasTrackedComplete.current
              ) {
                trackEvent('DashboardOrientationVideoCompleted', 1, {
                  clickType: 'interaction',
                  clickCTA: 'Video Completed',
                  clickSection: 'Dashboard Orientation Section',
                });
                hasTrackedComplete.current = true;
              }
            },
          },
        });
      };

      // Initialize player when API is ready
      if (globalThis.YT?.Player) {
        initPlayer();
      } else {
        globalThis.onYouTubeIframeAPIReady = initPlayer;
      }
    }

    // Cleanup player on drawer close
    return () => {
      if (playerRef.current) {
        playerRef.current.destroy();
        playerRef.current = null;
      }
    };
  }, [isOpen, trackEvent]);

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      contentClassName="bg-magical-moonlight-white"
      childClass="!max-w-full"
      topOffset="top-[10%]"
      closeButtonPosition={{ top: 'top-[calc(10%-45px)]', right: 'right-6' }}
      innerContentClassName="p-4 md:p-6"
      role="dialog"
      aria-modal="true"
    >
      <div className="w-full max-w-4xl mx-auto">
        <h2
          id="orientation-drawer-title"
          className="text-xl md:text-2xl font-semibold text-magical-moonlight-d1 mb-4 md:mb-6"
        >
          Dashboard Orientation
        </h2>

        <div className="overflow-hidden rounded-2xl">
          <iframe
            id={iframeId}
            width="100%"
            className="aspect-[16/9]"
            src={`https://www.youtube-nocookie.com/embed/${ORIENTATION_VIDEO_ID}${YOUTUBE_EMBED_PARAMS}&enablejsapi=1`}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            title="Dashboard Orientation Tutorial"
            aria-label="Dashboard Orientation Tutorial Video"
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          />
        </div>

        <p className="text-sm text-magical-moonlight-d1/70 mt-4">
          Watch this video to get familiar with all the features available on
          your dashboard.
        </p>
      </div>
    </Drawer>
  );
};
